import { z } from 'zod'

import { ConceptChipTypeSchema } from '~/@entities/ConceptChip'
import { ProblemSchema, ProblemTypeSchema } from '~/@entities/Problem'
import { ScoringResultSchema } from '~/@entities/Scoring'
import { StudentIdSchema } from '~/@entities/Student'

/***************** 성취도 등급 *****************/
// 순서가 의미를 갖는다. 낮은 등급부터 높은 등급 순이고, 화면의 필터 나열과 점수 계산(indexOf 기반)이
// 이 순서에 매여 있다. 값을 더하거나 옮기면 그 두 곳이 함께 바뀐다.
export const ACHIEVEMENT_LEVEL = [
  'WHITE', // 풀지 않음
  'GRAY', // 문제 수 부족 (1문제만 푼 경우)
  'SAD',
  'RED',
  'YELLOW',
  'GREEN',
  'SMILE',
] as const

// 학생이 한 유형칩에서 받은 성취도 등급. 유형칩 자체가 아니라 학생의 학습 결과라 ConceptChip 과 따로 둔다.
export const AchievementLevelSchema = z.enum(ACHIEVEMENT_LEVEL)
export type AchievementLevel = z.infer<typeof AchievementLevelSchema>

// "유형칩 + 그 학생의 성취도" 한 건. 학생 홈 요약(주간 정보·스마일 챌린지·최근 챌린지)이 이 모양을
// 코드 표기로 내주고 adapter 가 도메인 어휘로 푼다.
export const ConceptChipAchievementSchema = z.object({
  conceptChipId: z.number(),
  conceptId: z.number(),
  conceptName: z.string(),
  littleChapterId: z.number(),
  achievementLevel: AchievementLevelSchema,
  conceptChipType: ConceptChipTypeSchema,
})
export type ConceptChipAchievement = z.infer<typeof ConceptChipAchievementSchema>

/***************** 학생 한 명의 성취도 *****************/

// 학생 한 명이 유형칩 하나에서 받은 성취도. 그룹 성취도 응답이 학생 목록으로 싣는 모양이다.
export const StudentChipAchievementSchema = z.object({
  studentId: StudentIdSchema,
  studentName: z.string(),
  achievementLevel: AchievementLevelSchema,
})
export type StudentChipAchievement = z.infer<typeof StudentChipAchievementSchema>

// 개인 성취도 조회는 여기에 칩 id 와 가장 최근 채점 기록을 더한다. title 은 그 문항의 출처
// (`{학습물 제목}[ {페이지}] {문항 번호}`)다. 서버는 채점 기록이 있는 칩만 내므로 기록은 null 이 아니다.
export const StudentConceptChipAchievementSchema = StudentChipAchievementSchema.extend({
  conceptChipId: z.number(),
  latestStudyLog: z.object({
    datetime: z.string(),
    title: z.string(),
  }),
})
export type StudentConceptChipAchievement = z.infer<typeof StudentConceptChipAchievementSchema>

// 과거 이력(주차 스냅샷)의 한 건. 최근 채점 기록 대신 그 주까지 푼 횟수와 정오답 횟수를 싣는다.
export const StudentConceptChipAchievementLogSchema = StudentChipAchievementSchema.extend({
  conceptChipId: z.number(),
  totalUsed: z.number(),
  correctTimes: z.number(),
  wrongTimes: z.number(),
})
export type StudentConceptChipAchievementLog = z.infer<
  typeof StudentConceptChipAchievementLogSchema
>

// 유형칩 하나에서 학생이 최근 채점한 문항. 출처 학습물(학습지·교재·자동시험)과 학생 답, 문항을 함께 싣는다.
// subType 은 학습물 종류마다 다른 서버 코드 숫자다.
export const STUDENT_ACHIEVEMENT_SOURCE_TYPE = ['WORKSHEET', 'WORKBOOK', 'CAS'] as const

export const ConceptChipScoredItemSchema = z.object({
  type: z.enum(STUDENT_ACHIEVEMENT_SOURCE_TYPE),
  subType: z.number(),
  contentTitle: z.string(),
  contentProblemNumber: z.string(),
  contentPageNumber: z.string().nullable(),
  result: ScoringResultSchema,
  userAnswer: z.string().nullable(),
  datetime: z.iso.datetime({ local: true }),
  problem: z.object({
    problemId: z.number().nullable(),
    type: ProblemTypeSchema,
    answer: z.string(),
    problemImageUrl: z.string().nullable(),
    answerImageUrl: z.string().nullable(),
    solutionImageUrl: z.string().nullable(),
  }),
  orderNumber: z.number(),
  handwrittenNoteUrl: z.string().nullable(),
})
export type ConceptChipScoredItem = z.infer<typeof ConceptChipScoredItemSchema>

/***************** 여러 학생의 성취도 요약 *****************/

const _AchievementLevelCountsSchema = z.object({
  grayCount: z.number(),
  sadCount: z.number(),
  redCount: z.number(),
  yellowCount: z.number(),
  greenCount: z.number(),
  smileCount: z.number(),
})
export type AchievementLevelCounts = z.infer<typeof _AchievementLevelCountsSchema>

// 유형칩 하나에서 학생들의 등급별 인원. 아무도 풀지 않은 칩은 평균 등급·표준편차가 null 이고, 화면은 평균 등급 대신
// 과반이 미달성이면 GRAY 로 내리는 businessAverageAchievementLevel 을 쓴다.
export const ConceptChipAchievementSummarySchema = _AchievementLevelCountsSchema.extend({
  conceptChipId: z.number(),
  totalCount: z.number(),
  whiteCount: z.number(),
  averageAchievementLevel: AchievementLevelSchema.nullable(),
  businessAverageAchievementLevel: AchievementLevelSchema,
  standardDeviation: z.number().nullable(),
})
export type ConceptChipAchievementSummary = z.infer<typeof ConceptChipAchievementSummarySchema>

// 학생 한 명의 유형칩 난이도(개념 A·기본 B·심화 C)별 등급 인원
export const StudentAchievementSummarySchema = z.object({
  studentId: StudentIdSchema,
  onA: _AchievementLevelCountsSchema,
  onB: _AchievementLevelCountsSchema,
  onC: _AchievementLevelCountsSchema,
})
export type StudentAchievementSummary = z.infer<typeof StudentAchievementSummarySchema>

export const StudentsAchievementSummarySchema = z.object({
  chipSummaries: z.array(ConceptChipAchievementSummarySchema),
  studentSummaries: z.array(StudentAchievementSummarySchema),
})
export type StudentsAchievementSummary = z.infer<typeof StudentsAchievementSummarySchema>

/***************** 그룹(학년·반) 성취도 *****************/

// 그룹 성취도 과거 이력 한 건 — 주차 스냅샷이라 학생별 성취도 없이 칩과 평균색만 담는다.
export const GroupConceptChipAchievementLogSchema = z.object({
  conceptChipId: z.number(),
  conceptChipType: ConceptChipTypeSchema,
  recommended: z.boolean(),
  orderingNumber: z.number(),
  bigChapterId: z.number(),
  bigChapterName: z.string(),
  middleChapterId: z.number(),
  middleChapterName: z.string(),
  littleChapterId: z.number(),
  littleChapterName: z.string(),
  conceptId: z.number(),
  conceptName: z.string(),
  averageAchievementLevel: AchievementLevelSchema,
  repProblem: ProblemSchema.nullable(),
})
export type GroupConceptChipAchievementLog = z.infer<typeof GroupConceptChipAchievementLogSchema>

// 목록은 같은 칩 정보에 학생별 성취도를 더해 내준다.
export const GroupConceptChipAchievementSchema = GroupConceptChipAchievementLogSchema.extend({
  achievements: z.array(StudentChipAchievementSchema),
})
export type GroupConceptChipAchievement = z.infer<typeof GroupConceptChipAchievementSchema>

// 그룹 상세가 내주는 정답률 상·하위 문제. 문제은행의 Problem 이 아니라 이 응답 전용 집계 모양이다.
export const HighestRateProblemSchema = z.object({
  rank: z.number(),
  conceptName: z.string(),
  groupCorrectRate: z.number(),
  problemImageUrl: z.string().nullable(),
  answerImageUrl: z.string().nullable(),
  solutionImageUrl: z.string().nullable(),
})
export type HighestRateProblem = z.infer<typeof HighestRateProblemSchema>

export const GroupConceptChipDetailSchema = z.object({
  conceptChipId: z.number(),
  conceptChipType: ConceptChipTypeSchema,
  conceptName: z.string(),
  averageCorrectRate: z.number(),
  averageAchievementLevel: AchievementLevelSchema,
  achievements: z.array(StudentChipAchievementSchema),
  highestCorrectRateProblems: z.array(HighestRateProblemSchema),
  highestWrongRateProblems: z.array(HighestRateProblemSchema),
})
export type GroupConceptChipDetail = z.infer<typeof GroupConceptChipDetailSchema>
